import {Injectable} from '@angular/core';
import {ConnectorService} from './connector.service';
import {forkJoin, map, Observable, Subject, switchMap, takeUntil, tap} from 'rxjs';
import {
    ContractModel,
    ContractWrapperModel,
    IContractPaginationResponse,
    PaymentTransactionModel
} from '../../models/contract.model';
import {PaginationModel} from '../../models/file-table.model';
import {IUserData} from 'src/app/models/user.model';

@Injectable({
    providedIn: 'root'
})
export class ContractsService extends ConnectorService {
    /** AAA
     * Все доступные контракты сразу через максимальную пагинацию
     * @param pagination - пагинация и сортировка
     * @param guid
     * @param isAdmin
     * @returns
     */
    public subFirstPageContractData: Subject<IContractPaginationResponse> = new Subject()
    public subStopgetAll: Subject<boolean> = new Subject() // doesn't work yet

    public getAllatOnce(pagination: PaginationModel, guid: string, isAdmin: boolean,limit:number=300): Observable<ContractModel[]> {
        let reqBulk: Observable<ContractModel[]>[] = []
        return this.getAll(pagination, guid, isAdmin).pipe(
            tap(res => this.subFirstPageContractData.next(res)),
            tap(res => {
                let max_page_count = res.page_count ? Math.min(Math.ceil(limit / pagination.limit), res.page_count) : 1;
                for (let index = res.current_page; index <= max_page_count; index++) {
                    reqBulk.push(this.getAll({
                        ...pagination,
                        current_page: index
                    }, guid, isAdmin).pipe(map(res => res.data)))
                }
            }),
            switchMap(() => forkJoin(reqBulk)),
            map(res => res.flat())
        )
    }
    public getAllMembersContractatOnce(pagination: PaginationModel, guid: string, isAdmin: boolean,limit:number=300): Observable<ContractModel[]> {
        let reqBulk: Observable<ContractModel[]>[] = []
        return this.getAllMembersContract(pagination, guid).pipe(
            tap(res => this.subFirstPageContractData.next(res)),
            tap(res => {
                let max_page_count = Math.min(Math.ceil(limit / pagination.limit),res.page_count)
                for (let index = res.current_page; index <= max_page_count; index++) {
                    reqBulk.push(this.getAllMembersContract({
                        ...pagination,
                        current_page: index
                    }, guid).pipe(map(res => res.data)))
                }
            }),
            switchMap(() => forkJoin(reqBulk)),
            map(res => res.flat())
        )
    }

    /**
     * Все доступные контракты
     * @param pagination - пагинация и сортировка
     * @param guid
     * @param isAdmin
     * @returns
     */
    public getAll(pagination: PaginationModel, guid:string, isAdmin:boolean): Observable<IContractPaginationResponse> {
        return this.get('instance/eft/contracts/?current_page=' + pagination.current_page +
            '&limit=' + pagination.limit +
            '&sort_by=' + pagination.sort_by +
            '&sort=' + pagination.sort +
            '&statuses_at=' + pagination.status +
            (!isAdmin ? '&members__member__guid=' + guid : ''));
    }

    /**
     * Получение всех контрактов пользователя из всех проектов
     * @param pagination
     * @param guid
     * @returns
     */
    public getAllMembersContract(pagination:PaginationModel, guid:string): Observable<IContractPaginationResponse> {
        return this.get('instance/eft/contracts/' + guid, pagination);
    }

    /**
     * Получение контрактов, которые можно использовать в качестве субконтракта
     * @param pagination
     * @param contractGuid
     * @returns
     */
    public getAvailableSubcontracts(pagination:PaginationModel, contractGuid:string): Observable<IContractPaginationResponse> {
        return this.get('instance/eft/contracts/' + contractGuid + '/available-subcontracts/?current_page=' + pagination.current_page +
            '&limit=' + pagination.limit +
            '&sort_by=' + pagination.sort_by +
            '&sort=' + pagination.sort);
    }

    /**
     * Контракт по guid
     *
     * @param guid
     */
    public getByGuid(guid: string): Observable<{ contract: ContractWrapperModel }> {
        return this.get('instance/eft/contract/' + guid);
    }

    /**
     * создание контракта
     *
     * @param data
     */
    public create(data): Observable<{ contract: ContractWrapperModel, message: string }> {
        return this.post('instance/eft/contract/', data, false);
    }

    /**
     * обновление контракта
     *
     * @param contractGuid
     * @param data
     */
    public editContract(contractGuid: number|string, data: any): Observable<{ contract: ContractWrapperModel, message: string }> {
        return this.put('instance/eft/contract/' + contractGuid, data);
    }

    /**
     * удаление контракта
     *
     * @param contractGuid
     */
    public deleteContract(contractGuid: string): Observable<{ message: string }> {
        return this.delete('instance/eft/contract/' + contractGuid);
    }

    /**
     * Подписание контракта
     * @param isVerify
     * @param contractGuid
     * @param pin
     */
    public signature(isVerify: boolean, contractGuid: string, pin: string): Observable<{
        contract: ContractWrapperModel
    }> {
        if (isVerify) {
            return this.post('instance/eft/contract/' + contractGuid + '/verify', {PIN_code: pin});
        } else {
            return this.post('instance/eft/contract/' + contractGuid + '/cancel-sign', {PIN_code: pin});
        }
    }

    /**
     * Сохранить изменения в роадмапе
     * @param contractGuid
     * @param roadmap
     */
    public updateRoadMap(contractGuid, roadmap): Observable<{ contract: ContractWrapperModel, message: string }> {
        return this.put('instance/eft/contract/' + contractGuid + '/roadmap', roadmap, false);
    }

    /**
     * Дополнение степов роадмапа в которых не указан таргет если контракт подписан.
     * @param data
     * @returns
     */
    public fillStep(data): Observable<{ contract: ContractWrapperModel, message: string }> {
        return this.put('instance/eft/contract/roadmap/step/target/', data);
    }


    /**
     * Добавление пользователя в контракт
     * @param contractGuid
     * @param data
     */
    public addMemberToContract(contractGuid: string, data: IUserData): Observable<{
        contract: ContractWrapperModel,
        message: string
    }> {
        return this.post('instance/eft/contract/' + contractGuid + '/member', data);
    }


    /**
     * Обновление пользователя в контракте
     * @param contractGuid
     * @param data
     */
    public updateMemberToContract(contractGuid, data): Observable<{ contract: ContractWrapperModel, message: string }> {
        return this.put('instance/eft/contract/' + contractGuid + '/member', data);
    }

    /**
     * Удаление пользователя из контракта
     * @param memberGuid
     * @param contractGuid
     */
    public deleteMemberFromContract(memberGuid, contractGuid): Observable<{
        contract: ContractWrapperModel,
        message: string
    }> {
        return this.delete('instance/eft/contract/' + contractGuid + '/member/' + memberGuid);
    }

    /**
     * Создание платежки между двумя проектами в контракте
     * @param guid
     * @param data
     */
    public createContractPayment(guid: string, data): Observable<{
        transaction: PaymentTransactionModel,
        message: string
    }> {
        return this.post('instance/eft/contract/' + guid + '/transaction', data);
    }

    /**
     * Клонирование контракта
     * @param contractGuid
     */
    public clone(contractGuid): Observable<{ contract: ContractWrapperModel }> {
        return this.post('instance/eft/contract/' + contractGuid + '/clone');
    }

    /**
     * Перезаключение контракта
     * @param contractGuid
     */
    public renew(contractGuid): Observable<{ contract: ContractWrapperModel }> {
        return this.post('instance/eft/contract/' + contractGuid + '/renegotiation');
    }

    /**
     * Смена кошелька проекта
     * @param contractGuid
     * @param walletGuid
     */
    changeContractWallet(contractGuid, walletGuid): Observable<{ contract: ContractWrapperModel, message: string }> {
        return this.post('instance/eft/contract/' + contractGuid + '/project/wallet', {wallet_guid: walletGuid});
    }

    /**
     * Подписание ТАРГЕТА степа (на данный момент реализовано только с тасками)
     * @param targetKey
     * @param targetType
     * @param data
     */
    signTask(targetKey: string, targetType: string, data: { contract_guid: string, PIN_code: string }): Observable<{
        message: string
    }> {
        return this.post('instance/eft/contract/roadmap/target/' + targetType + '/' + targetKey + '/sign', data);
    }

    /**
     * Отзыв подписи ТАРГЕТА степа (на данный момент реализовано только с тасками)
     * @param targetKey
     * @param targetType
     * @param data
     */
    terminateTask(targetKey: string,
                  targetType: string,
                  data: {
                      contract_guid: string,
                      PIN_code: string
                  }): Observable<{ message: string }> {
        return this.post('instance/eft/contract/roadmap/target/' + targetType + '/' + targetKey + '/cancel-sign', data);
    }

    /**
     * Установить, удалить или обновить значение проперти
     * @param contractGuid
     * @param propertyId
     * @param value
     */
    setOrUpdateProperty(contractGuid: string, propertyId: number, value: { value: any }): Observable<any> {
        return this.put('instance/eft/custom/property/' + contractGuid + '/' + propertyId, value);
    }
}
